<template>
  <section>
    <div class="content-wrapper">

      <errors v-if="isVisibleErrorsModal"></errors>

      <question v-if="display"></question>

      <bank-account-changed-warning
        :isVisible="isBankAccountChangedWarningVisible"
        :isAdminView="true"
        @close="hideBankAccountChangedWarning"
        @goToUpcomingTransactions="handleGoToUpcomingTransactions" />

      <div class="content-heading">
        {{accountType}} account
        <small data-localize="dashboard.WELCOME"></small>
      </div>

      <breadcrumb class="breadcrumb" v-bind:breadcrumbsData="breadcrumbData" />
      <br>

      <spinner v-if="userDetailLoading
        || accountUsersLoading
        || isNotesSegmentLoading
        || isFundingSourcesSegmentLoading
        || preferencesSpinner
        || isUpcomingTransactionsSegmentLoading
        || isRepresentativesLoading
        || beneficiariesSpinner"></spinner>

      <div class="row">
        <div class="col-lg-12">
          <div id="panelChart9" class="panel panel-default">
            <div class="panel-title top-margin">Account information</div>

            <div class="line-separator"></div>

            <div class="row">
              <div class="col-md-6">
                <div class="row zero-margin-x">
                  <div class="col-12">
                    <div>
                      <label>Account name:</label>
                    </div>
                  </div>
                </div>

                <div class="row zero-margin-x">
                  <div class="col-12">
                    <RestrictedSingleInput
                      @inputUpdated="onNewAccountNameUpdate"
                      :input="newAccountName"
                      :limit="40"
                    />
                    <span
                      v-if="newAccountName.trim().length === 0"
                      class="error-feedback"
                    >Required</span>
                  </div>
                </div>

                <div class="row space zero-margin-x">
                  <div class="col-12">
                    <div>
                      <label>Date created:</label>
                    </div>
                  </div>
                </div>

                <div class="row zero-margin-x">
                  <div class="col-12">
                    <div>
                      <p> {{formatDate(account.createdAt)}} </p>
                    </div>
                  </div>
                </div>

                <div class="row space zero-margin-x" v-if="account.investorSince">
                  <div class="col-12">
                    <div>
                      <label>Investor since:</label>
                    </div>
                  </div>
                </div>

                <div class="row zero-margin-x" v-if="account.investorSince">
                  <div class="col-12">
                    <div>
                      <p> {{account.investorSince}} </p>
                    </div>
                  </div>
                </div>

                <div class="row space zero-margin-x">
                  <div class="col-12">
                    <div>
                      <label>Legacy username:</label>
                    </div>
                  </div>
                </div>


                <div class="row zero-margin-x">
                  <div class="col-12">
                    <div>
                      <p> {{account.legacyUsername}} </p>
                    </div>
                  </div>
                </div>
                <div class="row zero-margin-x">
                  <div class="col-12" >
                  <br/>
                    <button class="btn btn-default btn-lg" v-bind:disabled="disableSubmitButton()" v-on:click="showUpdateAccountConfirmationModal()">
                        Submit account information
                    </button>
                  </div>
                </div>
              </div>


              <div class="col-md-6">
                <div class="row zero-margin-x">
                  <div class="col-12">
                    <div>
                      <label>ID:</label>
                    </div>
                  </div>
                </div>

                <div class="row zero-margin-x">
                  <div class="col-12">
                    <div>
                      <p> {{account.accountId}} </p>
                    </div>
                  </div>
                </div>

                <div class="row space zero-margin-x">
                  <div class="col-12">
                    <div>
                      <label>Status:</label>
                    </div>
                  </div>
                </div>

                <div class="row zero-margin-x">
                  <div class="col-12">
                    <div>
                      <p v-if="account.status == 'active'" class="label label-success capitalize-text"> {{account.status}} </p>
                      <p v-if="account.status == 'pending'" class="label label-warning capitalize-text"> {{account.status}} </p>
                      <p v-if="account.status == 'inactive'" class="label label-danger capitalize-text"> {{account.status}} </p>
                      <p v-if="account.hasUserComments" class="label label-danger capitalize-text"> Comments </p>
                    </div>
                  </div>
                </div>

                <div class="row space zero-margin-x">
                  <div class="col-12">
                    <div>
                      <label>Legacy customer number:</label>
                    </div>
                  </div>
                </div>

                <div class="row zero-margin-x">
                  <div class="col-12">
                    <div>
                      <p> {{account.legacyCustomerNumber}} </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row" v-if="accountRepresentatives !== undefined && account.primaryInvestorId">
        <div class="col-lg-12">
          <AccountRepresentatives
            :accountId="accountId"
            :accountName="accountName"
            :accountType="accountType"
            :representatives="accountRepresentatives"
            :primaryInvestorId="account.primaryInvestorId"
            @representativesLoadingStarted="setRepresentativesSpinnerOn"
            @representativesLoadingSucceed="setRepresentativesSpinnerOff"
            @representativesLoadingFailed="setRepresentativesSpinnerOff"
            @representativesUpdated="loadAccountInformation" />
        </div>
      </div>

      <div class="row" v-if="beneficiaries !== undefined">
        <div class="col-lg-12">
          <AccountBeneficiaries
            :beneficiaries="beneficiaries"
            :accountId="accountId"
            @beneficiaryLoadingStarted="setBeneficiariesSpinnerOn"
            @beneficiaryLoadingSucceed="setBeneficiariesSpinnerOff"
            @beneficiaryLoadingFailed="setBeneficiariesSpinnerOff"
            @beneficiariesUpdated="loadAccountInformation" />
        </div>
      </div>

      <div class="row">
        <div class="col-lg-12">
          <AccountNotes
            :accountId="accountId"
            :displayAlertForUpcomingTransactionsWithAction="shouldUpcomingTransactionsWithActionsBeDisplayed"
            :displayAlertForUpcomingTransactionsWithNoAction="shouldUpcomingTransactionsWithNoActionsBeDisplayed"
            :disable-investment="user.disableInvestment"
            @goToUpcomingTransactionsSegment="goToSegment('upcoming-transactions-segment')"
            @notesLoadingFailed="setNotesSpinnerOff"
            @notesLoadingStarted="setNotesSpinnerOn"
            @notesLoadingSucceed="setNotesSpinnerOff"
            @redirectToNoteDetailsView="goToViewDetail"
            @redirectToPurchaseInvestmentNoteView="goToPurchaseInvestmentNote" />
        </div>
      </div>

      <div id="funding-sources-segment" class="row" v-if="account.defaultFundingSourceId !== undefined">
        <div class="col-lg-12">
          <AccountFundingSources
            :accountId="accountId"
            :defaultFundingSourceId="userAccount.defaultFundingSourceId"
            :account="account"
            @fundingSourcesLoadingFailed="setFundingSourceSpinnerOff"
            @fundingSourcesLoadingStarted="setFundingSourceSpinnerOn"
            @fundingSourcesLoadingSucceed="setFundingSourceSpinnerOff"
            @defaultFundingSourceUpdated="handleDefaultFundingSourceUpdated"
            @restart="loadAccountUsers"
            @showBankAccountChangedWarning="showBankAccountChangedWarning" />
        </div>
      </div>

      <div class="row">
        <div class="col-lg-12">
          <AdminAccountDocuments />
        </div>
      </div>

      <div class="row" id="preferences-segment">
        <div class="col-lg-12">
          <div id="panelChart9" class="panel panel-default">
            <div class="panel-title">
              Preferences
            </div>

            <SuccessAlert
              :isVisible="isPreferencesSuccessMessageVisible"
              :message="preferencesSuccessMessage"
              @hideSuccessAlert="hidePreferencesSuccessMessage" />

            <div class="line-separator"></div>

            <AccountPreferences
              v-if="account.communicationPreference !== undefined"
              :accountId="accountId"
              :communicationPreference="account.communicationPreference"
              :interestPreference="account.interestPreference"
              :paymentPreference="account.paymentPreference"
              :communication1099Preference="account.communication1099Preference"
              :communicationImpactReportPreference="account.communicationImpactReportPreference"
              :user="user"
              :redemptionRestriction="account.redemptionRestriction"
              @impactPreferencesLoadingFailed="setPreferencesSpinnerOff"
              @impactPreferencesLoadingStarted="setPreferencesSpinnerOn"
              @impactPreferencesLoadingSucceed="setPreferencesSpinnerOff"
              @updateImpactPreferencesFailed="setPreferencesSpinnerOff"
              @updateImpactPreferencesStarted="setPreferencesSpinnerOn"
              @updateImpactPreferencesSucceed="displayImpactPreferenceSuccessfulNotification" />
          </div>
        </div>
      </div>

      <div id="upcoming-transactions-segment" class="row">
        <div class="col-lg-12">
          <UpcomingTransactions
            ref="upcomingTransactions"
            :accountId="accountId"
            :defaultFundingSourceId="userAccount.defaultFundingSourceId"
            :investorAccountIdentifier="userAccount.accountId"
            @goToFundingSourcesSegment="goToSegment('funding-sources-segment')"
            @hideAlertForUpcomingTransactions="hideUpcomingTransactionsAlerts"
            @showAlertForUpcomingTransactionsWithAction="displayUpcomingTransactionsWithActionAlert"
            @showAlertForUpcomingTransactionsWithNoAction="displayUpcomingTransactionsWithNoActionAlert"
            @upcomingTransactionsLoadingFailed="setUpcomingTransactionsSpinnerOff"
            @upcomingTransactionsLoadingStarted="setUpcomingTransactionsSpinnerOn"
            @upcomingTransactionsLoadingSucceed="setUpcomingTransactionsSpinnerOff"
            />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import {
    mapActions,
    mapMutations,
    mapState,
  } from 'vuex';

  import _ from 'lodash';
  import AccountFundingSources from '@/components/AccountFundingSources';
  import AccountNotes from '@/components/AccountNotes';
  import AccountPreferences from '@/components/AccountPreferences';
  import AccountRepresentatives from '@/components/AccountRepresentatives';
  import AccountBeneficiaries from '@/components/AccountBeneficiaries';
  import Errors from '@/components/modals/Errors';
  import Question from '@/components/modals/Question';
  import BankAccountChangedWarning from '@/components/modals/BankAccountChangedWarning';
  import SuccessAlert from '@/components/SuccessAlert';
  import UpcomingTransactions from '@/components/UpcomingTransactions';
  import breadcrumb from '@/components/Breadcrumb';
  import AdminAccountDocuments from '@/components/AdminAccountDocuments';
  import RestrictedSingleInput from '@/components/formControls/RestrictedSingleInput';

  import {
    HIDE_QUESTION_MODAL,
    SET_CREATION_SUCCEEDED,
    SET_ERRORS_MODAL_INFO,
    SHOW_CHECK_WIRE_MODAL,
    SHOW_ERRORS_MODAL,
    SHOW_QUESTION_MODAL,
  } from '@/store/mutation-types';

  import {
    formatDate,
    formatAmount,
    formatRate,
    formatTerm,
    formatType,
    formatPaymentPreference,
    findAccount,
  } from '@/util';
  import { UserTypes } from '@/data/static/userTypes';
  import spinner from '@/components/widgets/Spinner';

  import {
    Constants,
  } from '../data/static/constants';

  const mix = {
    methods: {
      formatDate,
      formatAmount,
      formatRate,
      formatTerm,
      formatType,
      formatPaymentPreference,
      findAccount,
    },
  };

  export default {
    name: 'editJointAccount',
    beforeDestroy() {
      this.hidePreferencesSuccessMessage();
    },
    beforeMount() {
      this.setParams();
      this.loadUserDetail();
      this.loadAccountUsers();
    },
    mixins: [mix],
    components: {
      UpcomingTransactions,
      AccountFundingSources,
      AccountNotes,
      AccountPreferences,
      AccountRepresentatives,
      AccountBeneficiaries,
      errors: Errors,
      question: Question,
      BankAccountChangedWarning,
      spinner,
      SuccessAlert,
      breadcrumb,
      AdminAccountDocuments,
      RestrictedSingleInput,
    },
    watch: {
      user: function changedUser(newUser) {
        this.setBreadcrumbData(this.account, newUser);
      },
      account: function changedAccount(newAccount) {
        this.setBreadcrumbData(newAccount, this.user);
      },
    },
    data() {
      return {
        accountId: '',
        accountType: 'Joint',
        account: {},
        accountUsersLoading: false,
        breadcrumbData: [],
        currentAmountBalance: 0,
        fundingSources: [],
        isBankAccountChangedWarningVisible: false,
        isFundingSourcesSegmentLoading: false,
        isNotesSegmentLoading: false,
        isPreferencesSuccessMessageVisible: false,
        isUpcomingTransactionsSegmentLoading: false,
        isRepresentativesLoading: false,
        maxAmountFundingSources: 6,
        name: '',
        newAccountName: '',
        noteLoading: false,
        notes: [],
        notesError: '',
        preferencesSpinner: false,
        preferencesSuccessMessage: '',
        previousFundingSourceId: '',
        previousPaymentPreference: '',
        shouldUpcomingTransactionsWithActionsBeDisplayed: false,
        shouldUpcomingTransactionsWithNoActionsBeDisplayed: false,
        user: [],
        userAccount: { defaultFundingSourceId: '' },
        userDetailLoading: false,
        userId: '',
        accountRepresentatives: [],
        beneficiaries: [],
        beneficiariesSpinner: false,
      };
    },
    computed: {
      ...mapState('errors', [
        'isVisibleErrorsModal',
      ]),
      ...mapState('home', [
        'userStatus',
      ]),
      ...mapState('app', [
        'accountTypes',
      ]),
      ...mapState('question', [
        'display',
      ]),
      ...mapState('checkWireModal', [
        'isCheckWireModalVisible',
        'creationSucceeded',
      ]),
      accountName() {
        return this.account.accountName;
      },
    },
    methods: {
      ...mapActions('account', [
        'getAccount',
        'getAccountUsers',
        'getAccountNotes',
        'getAccountFundingSources',
        'updateAccountPaymentPreference',
        'updateAccountName',
        'setDefaultFundingSource',
        'getAccountBeneficiaries',
      ]),
      ...mapActions('accountCreation', [
        'getUserDetail',
      ]),
      ...mapMutations('question', {
        hideQuestionModal: HIDE_QUESTION_MODAL,
        showQuestionModal: SHOW_QUESTION_MODAL,
      }),
      ...mapMutations('errors', {
        showErrorsModal: SHOW_ERRORS_MODAL,
        setErrorsModalInfo: SET_ERRORS_MODAL_INFO,
      }),
      ...mapMutations('checkWireModal', {
        showCheckWireModal: SHOW_CHECK_WIRE_MODAL,
        setCreationStatus: SET_CREATION_SUCCEEDED,
      }),
      setBreadcrumbData(account, user) {
        this.breadcrumbData = [];
        this.breadcrumbData.push(
          Object.assign({}, {
            title: 'Search',
            goto: { name: 'Users' },
            selected: false,
          }),
        );
        const params = Object.assign({}, this.$route.params);
        let goto;
        if (user.userType === UserTypes.FINANCIAL_ADVISOR.name) {
          goto = { name: 'EditFinancialAdvisor', params: { name: params.name, userId: params.userId } };
        } else {
          goto = { name: 'UserDetail', params: { name: params.name, userId: params.userId } };
        }

        const title = user.fullName;
        this.breadcrumbData.push(
          Object.assign({}, {
            title,
            goto,
            selected: false,
          }),
        );
        const gotoAccount = { name: this.$route.name, params };
        this.breadcrumbData.push(
          Object.assign({}, {
            title: account.accountName,
            goto: gotoAccount,
            selected: true,
          }),
        );
      },
      displayImpactPreferenceSuccessfulNotification() {
        this.preferencesSuccessMessage = Constants.adminPreferences.successMessage;
        this.isPreferencesSuccessMessageVisible = true;
        this.preferencesSpinner = false;
        this.goToSegment('preferences-segment');
      },
      displayUpcomingTransactionsWithActionAlert() {
        this.shouldUpcomingTransactionsWithActionsBeDisplayed = true;
        this.shouldUpcomingTransactionsWithNoActionsBeDisplayed = false;
      },
      displayUpcomingTransactionsWithNoActionAlert() {
        this.shouldUpcomingTransactionsWithActionsBeDisplayed = false;
        this.shouldUpcomingTransactionsWithNoActionsBeDisplayed = true;
      },
      goToSegment(segment) {
        document.getElementById(segment).scrollIntoView();
      },
      hideUpcomingTransactionsAlerts() {
        this.shouldUpcomingTransactionsWithActionsBeDisplayed = false;
        this.shouldUpcomingTransactionsWithNoActionsBeDisplayed = false;
      },
      setFundingSourceSpinnerOff() {
        this.isFundingSourcesSegmentLoading = false;
      },
      setFundingSourceSpinnerOn() {
        this.isFundingSourcesSegmentLoading = true;
      },
      setNotesSpinnerOff() {
        this.isNotesSegmentLoading = false;
      },
      setNotesSpinnerOn() {
        this.isNotesSegmentLoading = true;
      },
      setParams() {
        this.userId = this.$route.params.userId;
        this.accountId = this.$route.params.accountId;
        this.name = this.$route.params.name;
      },
      setPreferencesSpinnerOff() {
        this.preferencesSpinner = false;
      },
      setPreferencesSpinnerOn() {
        this.preferencesSpinner = true;
      },
      setUpcomingTransactionsSpinnerOff() {
        this.isUpcomingTransactionsSegmentLoading = false;
      },
      setUpcomingTransactionsSpinnerOn() {
        this.isUpcomingTransactionsSegmentLoading = true;
      },
      setRepresentativesSpinnerOn() {
        this.isRepresentativesLoading = true;
      },
      setRepresentativesSpinnerOff() {
        this.isRepresentativesLoading = false;
      },
      setBeneficiariesSpinnerOn() {
        this.beneficiariesSpinner = true;
      },
      setBeneficiariesSpinnerOff() {
        this.beneficiariesSpinner = false;
      },
      loadAccountInformation() {
        this.loadUserDetail();
        this.loadAccountUsers();
      },
      disableSubmitButton() {
        return this.newAccountName.length === 0;
      },
      hidePreferencesSuccessMessage() {
        this.isPreferencesSuccessMessageVisible = false;
      },
      loadUserDetail() {
        this.userDetailLoading = true;
        return this.getUserDetail(this.userId)
          .then((user) => {
            this.userDetailLoading = false;
            this.user = user;
            this.userAccount = this.findAccount(user.accounts, this.accountId);

            if (this.userAccount.accountType === this.accountTypes.UTMA_UGMA_ACCOUNT.name) {
              this.accountType = 'UTMA/UGMA';
            }
            this.newAccountName = this.userAccount.accountName;
            this.previousFundingSourceId = this.userAccount.defaultFundingSourceId;
          }).catch((err) => {
            this.userDetailLoading = false;
            if (err !== 'UNAUTHORIZED') {
              this.setErrorsModalInfo(['We could not retrieve user detail']);
              this.showErrorsModal();
            }
          });
      },
      async loadAccountUsers() {
        try {
          this.accountUsersLoading = true;
          const [account, accountRepresentatives, beneficiaries] = await Promise.all([
            this.getAccount(this.accountId),
            this.getAccountUsers(this.accountId),
            this.getAccountBeneficiaries(this.accountId),
          ]);
          this.accountRepresentatives = accountRepresentatives;
          this.account = account;
          this.beneficiaries = beneficiaries;
          this.previousPaymentPreference = this.account.paymentPreference;
          return Promise.resolve({});
        } catch (err) {
          if (err !== 'UNAUTHORIZED') {
            this.setErrorsModalInfo(['We could not retrieve users list']);
            this.showErrorsModal();
            return err;
          }
          return Promise.reject(err);
        } finally {
          this.accountUsersLoading = false;
        }
      },
      loadNotesList() {
        this.noteLoading = true;
        return this.getAccountNotes(this.accountId)
        .then((notes) => {
          this.noteLoading = false;
          this.notes = notes;
          const openNotes = _.filter(notes, { status: 'open' });
          this.currentAmountBalance = _.sumBy(['currentBalance'], _.partial(_.sumBy, openNotes));
        })
        .catch((err) => {
          this.noteLoading = false;
          if (err !== 'UNAUTHORIZED') {
            this.setErrorsModalInfo(['We could not retrieve notes list']);
            this.showErrorsModal();
          }
        });
      },
      loadFundingSourceAfterModalSave() {
        if (this.creationSucceeded) {
          this.loadUserDetail();
          this.loadFundingSources();
          this.setCreationStatus(false);
        }
      },
      loadFundingSources() {
        this.fundingLoading = true;
        return this.getAccountFundingSources(this.accountId)
        .then((data) => {
          this.fundingLoading = false;
          this.fundingSources = data.filter(fss => !fss.removed &&
          (fss.type === 'bank' ||
          fss.type === 'check' ||
          fss.type === 'wire/ach'));
        }).catch((err) => {
          this.fundingLoading = false;
          if (err !== 'UNAUTHORIZED') {
            this.setErrorsModalInfo(['We could not retrieve bank accounts list']);
            this.showErrorsModal();
          }
        });
      },
      getAccountType() {
        if (this.userAccount && this.userAccount.accountType) {
          const accountType = this.userAccount.accountType;
          return `${accountType[0]}${accountType.slice(1).toLowerCase()}`;
        }
        return '';
      },
      showUpdateAccountConfirmationModal() {
        this.showQuestionModal({
          title: 'Update Account Name',
          question: 'Are you sure about these changes?',
          method: () => {
            this.methodToExecuteInUpdateAccountQuestionModal();
          },
          yesButtonText: Constants.purchaseNote.modalYesButtonText,
          hideCancelButton: false,
        });
      },
      async methodToExecuteInUpdateAccountQuestionModal() {
        this.hideQuestionModal();
        this.accountUsersLoading = true;
        await this.updateAccountName({
          accountId: this.accountId,
          name: this.newAccountName,
        });
        this.loadAccountInformation();
        this.accountUsersLoading = false;
      },


      openCheckWireModal() {
        const filteredFundingSources = this.fundingSources.filter(fss => fss.type === 'check' ||
          fss.type === 'wire-transfer');
        this.showCheckWireModal({ accountId: this.accountId,
          fundingSources: filteredFundingSources });
      },
      goToPurchaseInvestmentNote() {
        const accountId = this.accountId;
        const userId = this.userId;
        const name = this.name;
        this.$router.push({ name: 'PurchaseInvestmentNote', params: { accountId, userId, name } });
      },
      goToViewDetail(note) {
        const userId = this.userId;
        const noteId = note.id;
        const name = this.name;

        const routeParams = { name: 'NoteDetail', params: { name, userId, noteId } };
        this.$router.push(routeParams);
      },
      onNewAccountNameUpdate(value) {
        this.newAccountName = value.trim();
      },
      async handleDefaultFundingSourceUpdated() {
        await Promise.all([
          this.loadUserDetail(),
          this.loadAccountUsers(),
        ]);
        await this.$nextTick();
        if (this.$refs.upcomingTransactions) {
          await this.$refs.upcomingTransactions.initializeState();
        }
      },
      showBankAccountChangedWarning() {
        this.isBankAccountChangedWarningVisible = true;
      },
      hideBankAccountChangedWarning() {
        this.isBankAccountChangedWarningVisible = false;
      },
      handleGoToUpcomingTransactions() {
        this.goToSegment('upcoming-transactions-segment');
      },
    },
  };
</script>

<style scoped>
  .line-separator {
    padding: 8px;
    line-height: 1.52857143;
    vertical-align: top;
    border-top: 1px solid #eee;
  }
  .zero-margin-x {
    margin-left: 0;
    margin-right: 0;
  }
  .left-margin {
    margin-left: 2%;
  }
  .right-margin {
    margin-left: -4%;
  }
  .top-margin {
    margin-top: 2%;
  }
  .space {
    margin-top: 4%;
  }
  .content-heading {
    font-weight: normal;
  }
  .panel-title {
    font-size: 20px;
    margin-bottom: 5px;
  }
  .panel-subtitle {
    font-size: 16px;
  }
  .panel-default {
    padding: 30px 40px;
  }
  .panel-heading {
    background-color: #ffffff;
    font-size: 16px;
    padding: 0px 5px;
  }
  label {
    color: #888;
    font-size: 14px;
    width: 250px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  table {
    border-collapse: collapse;
  }
  th {
    padding: 14px 8px;
    color: #888;
    border-bottom-width: 1px;
    vertical-align: bottom;
    border-bottom: 1px solid #eee;
    border-top: 0;
    line-height: 1.52857143;
    font-weight: normal;
  }
  td {
    padding: 8px;
    line-height: 1.52857143;
    vertical-align: top;
    border-top: 1px solid #eee;
  }
  .table-striped>tbody>tr:nth-of-type(odd) {
    background-color: #fafbfc;
  }
  .amountBalance {
    font-size: 20px;
  }
  .label {
    display: inline-block;
    padding: 4px 10px;
  }
  .label-name {
    color: #31b0d5;
  }
  .label-names {
    font-size: 20px;
  }
  .rows-9 thead th {
    width: 11.1%;
  }
  .rows-5 thead th {
    width: 25%;
  }
  .btn-back {
    color: #31b0d5;
    background-color: Transparent;
    background-repeat:no-repeat;
    border: none;
    cursor:pointer;
    overflow: hidden;
    outline:none;
    padding-left: 20px;
  }
  .icon-back {
    position:relative;
  }
  .icon-back:before {
    content: "\f060";
    font-family: FontAwesome;
    position:absolute;
    left: 0px;
  }
  .button-check-wire {
    background-color: white;
    border-color: #127285;
    color: #127285;
  }
  .capitalize-text {
    text-transform: capitalize;
  }
  @media only screen and (max-width: 1440px) {
    .table-fixed-header--md tr td:last-child {
      padding-right: 40px;
    }
    .table-fixed-header--md tr th:last-child {
      padding-right: 120px;
    }
  }
  .bank-account__radio-button {
    display: inline-block;
    margin-bottom: 0!important;
  }
</style>



// WEBPACK FOOTER //
// EditJointAccount.vue?06caa5b6